import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatDate, formatDateTime } from "../lib/api";
import { Mail, RefreshCw, Link2, Unlink, AlertCircle, X, Paperclip, Download, CheckCircle2, FileText, Search, Reply, Send, Bell, Plus, Users } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "action", label: "Action Needed" },
  { key: "lnt", label: "L&T" },
  { key: "RFQ/Quote", label: "RFQ / Quote" },
  { key: "Reverse Auction", label: "Reverse Auction" },
  { key: "Invoice/Payment", label: "Invoice / Payment" },
];

const priorityBadge = (p) =>
  p === "high" ? "bg-[#8A2526] text-white" : p === "medium" ? "bg-[#C84B31]/15 text-[#C84B31]" : "bg-[#E5E3DB] text-[#68665E]";

export default function Inbox() {
  const [status, setStatus] = useState(null);
  const [emails, setEmails] = useState([]);
  const [filter, setFilter] = useState("all");
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full email doc
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [deepSearching, setDeepSearching] = useState(false);
  const [lastSeen, setLastSeen] = useState(() => Number(localStorage.getItem("inbox_last_seen_ts") || 0));

  const newCount = emails.reduce((n, e) => (Number(e.internal_ts || 0) > lastSeen ? n + 1 : n), 0);
  function markSeen() {
    const maxTs = emails.reduce((m, e) => Math.max(m, Number(e.internal_ts || 0)), lastSeen);
    localStorage.setItem("inbox_last_seen_ts", String(maxTs));
    setLastSeen(maxTs);
    window.dispatchEvent(new Event("inbox-seen"));
  }

  async function loadStatus() {
    const { data } = await api.get("/gmail/status");
    setStatus(data);
    return data;
  }
  async function loadEmails(quiet = false) {
    if (!quiet) setLoading(true);
    try {
      const { data } = await api.get(`/gmail/messages?filter=${encodeURIComponent(filter)}&search=${encodeURIComponent(debouncedSearch)}`);
      setEmails(data);
    } catch (e) { if (process.env.NODE_ENV !== "production") console.error("Failed to load emails", e); }
    finally { if (!quiet) setLoading(false); }
  }

  // Search the ENTIRE Gmail mailbox (incl. old mail) via a server-side Gmail search,
  // then refresh the list once the matching mail has been pulled into the cache.
  async function searchAllMail() {
    const term = debouncedSearch.trim();
    if (!term) return;
    setDeepSearching(true);
    try {
      await api.post("/gmail/sync", { query: term, max_results: 40 });
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        const s = await loadStatus().catch(() => null);
        await loadEmails(true);
        if (!s?.syncing || tries > 12) { clearInterval(poll); setDeepSearching(false); }
      }, 2500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not search all mail");
      setDeepSearching(false);
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get("gmail");
    if (g === "connected") toast.success("Gmail connected!");
    else if (g) toast.error("Gmail connection failed — please try again");
    const openId = params.get("open");
    if (openId) setSelected({ id: openId });
    if (g || openId) window.history.replaceState({}, "", "/inbox");
    loadStatus();
  }, []);
  useEffect(() => { if (status?.connected) loadEmails(); }, [filter, debouncedSearch, status?.connected]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  // Quietly pull new mail every 30s in the background (no spinner, list order preserved;
  // new mail simply appears on top and existing mail shifts down). Paused while a modal is open.
  useEffect(() => {
    if (!status?.connected) return;
    const id = setInterval(() => { if (!selected && !deepSearching) { loadStatus(); loadEmails(true); } }, 30000);
    return () => clearInterval(id);
  }, [status?.connected, selected, deepSearching, filter, debouncedSearch]);
  // First ever visit: treat whatever is already in the inbox as "seen" so we don't
  // flag the entire backlog as new. After that, only genuinely newer mail counts.
  useEffect(() => {
    if (lastSeen === 0 && emails.length) {
      const maxTs = emails.reduce((m, e) => Math.max(m, Number(e.internal_ts || 0)), 0);
      if (maxTs) { localStorage.setItem("inbox_last_seen_ts", String(maxTs)); setLastSeen(maxTs); window.dispatchEvent(new Event("inbox-seen")); }
    }
  }, [emails, lastSeen]);

  async function connect() {
    try {
      const { data } = await api.get("/oauth/gmail/login");
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start Gmail connection"); }
  }
  async function disconnect() {
    if (!confirm("Disconnect Gmail? Synced emails stay, but no new ones will be pulled.")) return;
    await api.post("/gmail/disconnect");
    toast.success("Gmail disconnected");
    loadStatus();
  }
  // Manual sync: kick off background scan, then poll status until it finishes.
  async function triggerSync() {
    setSyncing(true);
    try {
      await api.post("/gmail/sync", { max_results: 30 });
      toast.success("Scanning your inbox & reading attachments…");
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        const s = await loadStatus().catch(() => null);
        await loadEmails();
        if (!s?.syncing || tries > 40) { clearInterval(poll); setSyncing(false); }
      }, 3000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start sync");
      setSyncing(false);
    }
  }

  if (status && status.configured === false) {
    return (
      <div data-testid="inbox-page">
        <Header />
        <div className="table-wrap p-8 text-center max-w-xl mx-auto mt-10" data-testid="inbox-not-configured">
          <AlertCircle className="w-10 h-10 text-[#C84B31] mx-auto mb-4" />
          <h2 className="font-headings text-xl font-bold text-[#1A1A18] mb-2">Gmail not set up yet</h2>
          <p className="text-sm text-[#68665E]">The server needs your Google OAuth Client ID &amp; Secret before you can connect Gmail.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="inbox-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <Header email={status?.connected ? status.email : null} connectedBy={status?.connected_by} />
        <div className="flex items-center gap-2">
          {status?.connected ? (
            <>
              {status?.syncing && (
                <span className="text-xs text-[#68665E] inline-flex items-center gap-1.5" data-testid="gmail-syncing-indicator"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Syncing in background…</span>
              )}
              {status?.is_admin && (
                <button className="btn-secondary" onClick={disconnect} data-testid="gmail-disconnect-btn"><Unlink className="w-4 h-4" /> Disconnect</button>
              )}
            </>
          ) : (
            status?.is_admin
              ? <button className="btn-primary" onClick={connect} data-testid="gmail-connect-btn"><Link2 className="w-4 h-4" /> Connect Gmail</button>
              : null
          )}
        </div>
      </div>

      {status?.last_error && status?.connected && (
        <div className="text-xs text-[#8A2526] bg-[#8A2526]/8 border border-[#8A2526]/20 px-3 py-2 mb-4 rounded" data-testid="inbox-last-error">
          Last sync issue: {status.last_error}
        </div>
      )}

      {!status?.connected ? (
        <div className="table-wrap p-8 text-center max-w-xl mx-auto" data-testid="inbox-connect-prompt">
          <Mail className="w-10 h-10 text-[#C84B31] mx-auto mb-4" />
          <h2 className="font-headings text-xl font-bold text-[#1A1A18] mb-2">Company inbox not connected</h2>
          <p className="text-sm text-[#68665E]">
            {status?.is_admin
              ? "Connect the company Gmail once. After that I automatically read new emails and their attachments in the background — telling you what's needed and letting you link a mail to an LR. No manual sync needed."
              : "The shared company inbox hasn't been connected yet. Please ask an admin to connect Gmail."}
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <div className="relative flex-1 min-w-[220px] max-w-md" data-testid="inbox-search-wrap">
              <Search className="w-4 h-4 text-[#68665E] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                className="input-field w-full pl-9 pr-8"
                placeholder="Search mails, doc / job numbers, attachments…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="inbox-search-input"
              />
              {search && (
                <button className="absolute right-2 top-1/2 -translate-y-1/2 text-[#68665E] hover:text-[#1A1A18]" onClick={() => setSearch("")} data-testid="inbox-search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {search && (
              <button className="btn-secondary shrink-0" onClick={searchAllMail} disabled={deepSearching} data-testid="inbox-search-all" title="Search your entire Gmail inbox, including old mail">
                {deepSearching ? <><RefreshCw className="w-4 h-4 animate-spin" /> Searching all mail…</> : <><Search className="w-4 h-4" /> Search all mail</>}
              </button>
            )}
          </div>

          {debouncedSearch && (
            <div className="text-[11px] text-[#68665E] mb-3" data-testid="inbox-search-hint">
              Showing cached matches for “{debouncedSearch}”. Click <span className="font-medium">Search all mail</span> to pull matching older emails from Gmail.
            </div>
          )}
          <div className="flex flex-wrap gap-2 mb-5" data-testid="inbox-filters">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`inbox-filter-${f.key}`}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${filter === f.key ? "bg-[#C84B31] text-white" : "bg-[#E5E3DB] text-[#68665E] hover:text-[#1A1A18]"}`}>
                {f.label}
              </button>
            ))}
          </div>

          {newCount > 0 && !debouncedSearch && (
            <button onClick={() => { window.scrollTo({ top: 0, behavior: "smooth" }); markSeen(); }} data-testid="inbox-new-pill"
              className="flex items-center gap-2 mb-4 px-4 py-2 rounded-full bg-[#DA1E28] text-white text-sm font-medium shadow-sm hover:bg-[#b91820] transition-colors">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
              {newCount} new {newCount === 1 ? "email" : "emails"} since you last looked — click to view
            </button>
          )}

          {loading ? (
            <div className="text-sm text-[#68665E]">Loading...</div>
          ) : emails.length === 0 ? (
            <div className="text-sm text-[#68665E]" data-testid="inbox-empty">{debouncedSearch ? `No emails match "${debouncedSearch}".` : "No emails yet. New mail will appear here automatically as it syncs in the background."}</div>
          ) : (
            <div className="space-y-3" data-testid="inbox-list">
              {emails.map((e) => (
                <button key={e.id} className="table-wrap p-4 w-full text-left hover:border-[#C84B31] transition-colors" onClick={() => setSelected(e)} data-testid={`email-row-${e.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${priorityBadge(e.ai_priority)}`}>{e.ai_priority}</span>
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#1A1A18] text-white">{e.ai_category}</span>
                    {e.ai_is_lnt && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#C84B31] text-white" data-testid={`email-lnt-${e.id}`}>L&amp;T</span>}
                    {e.linked_lr_number && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#2E5E3A] text-white flex items-center gap-1" data-testid={`email-linked-${e.id}`}><CheckCircle2 className="w-3 h-3" /> {e.linked_lr_number}</span>}
                    {(e.attachments?.length > 0) && <span className="text-[10px] text-[#68665E] flex items-center gap-1"><Paperclip className="w-3 h-3" />{e.attachments.length}</span>}
                    <span className="ml-auto text-[11px] text-[#68665E] whitespace-nowrap" data-testid={`email-time-${e.id}`}>{formatDateTime(e.internal_ts)}</span>
                  </div>
                  <div className="font-medium text-[#1A1A18] truncate" data-testid={`email-subject-${e.id}`}>{e.subject || "(no subject)"}</div>
                  <div className="text-xs text-[#68665E] truncate">{e.sender}</div>
                  <div className="text-sm text-[#1A1A18] mt-2">{e.ai_summary || e.snippet}</div>
                  {e.ai_action && e.ai_action !== "No action" && (
                    <div className="text-sm bg-[#C84B31]/8 border-l-2 border-[#C84B31] px-3 py-1.5 mt-2" data-testid={`email-action-${e.id}`}>
                      <span className="font-semibold text-[#C84B31]">Action: </span>{e.ai_action}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {selected && <EmailModal email={selected} onClose={() => setSelected(null)} onChanged={() => { loadEmails(true); }} />}
    </div>
  );
}

function EmailModal({ email: listEmail, onClose, onChanged }) {
  const [email, setEmail] = useState(listEmail);
  const [entities, setEntities] = useState({ lr: [], invoice: [], quotation: [], expense: [] });
  const [users, setUsers] = useState([]);
  const [linkType, setLinkType] = useState("lr");
  const [linkId, setLinkId] = useState("");
  const [linking, setLinking] = useState(false);
  const [thread, setThread] = useState([]);
  const [creatingLr, setCreatingLr] = useState(false);
  // reply
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [replyAll, setReplyAll] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [templates, setTemplates] = useState([]);
  // reminder
  const [showReminder, setShowReminder] = useState(false);
  const [remTitle, setRemTitle] = useState("");
  const [remDue, setRemDue] = useState("");
  const [remAssignee, setRemAssignee] = useState("");
  const [remSaving, setRemSaving] = useState(false);

  const ENTITY_TYPES = [
    { key: "lr", label: "LR" },
    { key: "invoice", label: "Invoice" },
    { key: "quotation", label: "Quotation" },
    { key: "expense", label: "Expense" },
  ];
  const entityLabel = (type, e) => {
    if (type === "lr") return `${e.lr_number} — ${e.consignor || e.hiring_company || ""}`;
    if (type === "invoice") return `${e.invoice_number} — ${e.customer_name || ""}`;
    if (type === "quotation") return `${e.quote_number} — ${e.customer_name || ""}`;
    if (type === "expense") return `${e.reason || e.category || "Expense"} — ₹${e.amount || 0}`;
    return e.id;
  };

  useEffect(() => {
    api.get(`/gmail/messages/${listEmail.id}`).then((r) => setEmail(r.data)).catch(() => {});
    api.get(`/gmail/messages/${listEmail.id}/thread`).then((r) => setThread(r.data || [])).catch(() => {});
    api.get("/lrs").then((r) => setEntities((s) => ({ ...s, lr: r.data || [] }))).catch(() => {});
    api.get("/invoices").then((r) => setEntities((s) => ({ ...s, invoice: r.data || [] }))).catch(() => {});
    api.get("/quotations").then((r) => setEntities((s) => ({ ...s, quotation: r.data || [] }))).catch(() => {});
    api.get("/expenses").then((r) => setEntities((s) => ({ ...s, expense: r.data || [] }))).catch(() => {});
    api.get("/garv/users").then((r) => setUsers(r.data || [])).catch(() => {});
    loadTemplates();
  }, [listEmail.id]);

  function loadTemplates() {
    api.get("/gmail/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }

  function applyTemplate(id) {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setReplyText((prev) => (prev.trim() ? prev + "\n\n" + t.body : t.body));
  }

  async function saveTemplate() {
    const body = replyText.trim();
    if (!body) { toast.error("Type a reply first, then save it as a template"); return; }
    const name = window.prompt("Name this reply template (e.g. Rate confirmation, POD request):");
    if (!name || !name.trim()) return;
    try {
      await api.post("/gmail/templates", { name: name.trim(), body });
      toast.success("Template saved");
      loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save template"); }
  }

  useEffect(() => { setLinkId(""); }, [linkType]);

  async function sendReply() {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      const { data } = await api.post(`/gmail/messages/${email.id}/reply`, { body: replyText, cc, bcc, reply_all: replyAll });
      const r = data.recipients || {};
      const extra = (r.cc?.length ? ` · Cc ${r.cc.length}` : "") + (r.bcc?.length ? ` · Bcc ${r.bcc.length}` : "");
      toast.success(`Reply sent to ${r.to || "sender"}${extra}`);
      setReplyText(""); setCc(""); setBcc(""); setShowReply(false); setReplyAll(false); setShowCcBcc(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reply"); }
    finally { setReplying(false); }
  }

  async function createLrFromEmail() {
    setCreatingLr(true);
    try {
      const { data } = await api.post(`/gmail/messages/${email.id}/create-lr`);
      const link = data.link;
      const links = [...(email.links || []).filter((l) => !(l.type === "lr" && l.id === link.id)), link];
      setEmail({ ...email, links, linked_lr_id: data.lr_id, linked_lr_number: data.lr_number });
      toast.success(`Created ${data.lr_number}${data.prefilled?.length ? ` — prefilled ${data.prefilled.length} field(s)` : ""} & linked`);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create LR"); }
    finally { setCreatingLr(false); }
  }

  async function download(att) {
    try {
      const res = await api.get(`/gmail/messages/${email.id}/attachments/${att.attachment_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || "attachment"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download attachment"); }
  }

  async function linkEntity() {
    if (!linkId) return;
    setLinking(true);
    try {
      const { data } = await api.post(`/gmail/messages/${email.id}/link`, { entity_type: linkType, entity_id: linkId });
      const links = [...(email.links || []).filter((l) => !(l.type === linkType && l.id === linkId)), data.link];
      setEmail({ ...email, links, ...(linkType === "lr" ? { linked_lr_id: linkId, linked_lr_number: data.link.label } : {}) });
      setLinkId("");
      toast.success(`Linked to ${data.link.label}${linkType === "lr" ? " — work marked started" : ""}`);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not link"); }
    finally { setLinking(false); }
  }
  async function unlinkEntity(l) {
    setLinking(true);
    try {
      await api.post(`/gmail/messages/${email.id}/unlink`, { entity_type: l.type, entity_id: l.id });
      const links = (email.links || []).filter((x) => !(x.type === l.type && x.id === l.id));
      setEmail({ ...email, links, ...(l.type === "lr" ? { linked_lr_id: null, linked_lr_number: null } : {}) });
      toast.success("Unlinked");
      onChanged();
    } catch { toast.error("Could not unlink"); }
    finally { setLinking(false); }
  }

  async function createReminder() {
    if (!remTitle.trim()) return;
    setRemSaving(true);
    try {
      await api.post("/garv/tasks", { title: remTitle, due_date: remDue, assignee: remAssignee, email_id: email.id });
      toast.success("Reminder created on the board");
      setRemTitle(""); setRemDue(""); setRemAssignee(""); setShowReminder(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create reminder"); }
    finally { setRemSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center py-8 overflow-y-auto" data-testid="email-modal">
      <div className="bg-white w-full max-w-3xl mx-4">
        <div className="px-6 py-4 border-b border-[#E5E3DB] flex items-start justify-between gap-4 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <h2 className="font-headings text-xl font-bold text-[#1A1A18] truncate">{email.subject || "(no subject)"}</h2>
            <div className="text-xs text-[#68665E] mt-1">{email.sender} · {formatDateTime(email.internal_ts) || formatDate(email.synced_at)}</div>
          </div>
          <button className="btn-ghost shrink-0" onClick={onClose} data-testid="email-modal-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid gap-2">
            <div className="text-sm"><span className="text-[#68665E]">AI summary: </span>{email.ai_summary}</div>
            {email.ai_action && email.ai_action !== "No action" && (
              <div className="text-sm bg-[#C84B31]/8 border-l-2 border-[#C84B31] px-3 py-2"><span className="font-semibold text-[#C84B31]">Action needed: </span>{email.ai_action}</div>
            )}
            {email.ai_extracted && Object.keys(email.ai_extracted).length > 0 && (
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#68665E]">
                {Object.entries(email.ai_extracted).map(([k, v]) => v ? <span key={k}><span className="uppercase tracking-wide">{k.replace("_", " ")}:</span> <span className="text-[#1A1A18] font-medium">{String(v)}</span></span> : null)}
              </div>
            )}
          </div>

          {/* Universal linking */}
          <div className="border border-[#E5E3DB] rounded p-4" data-testid="email-link-section">
            <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold mb-3">Linked records</div>
            {(email.links?.length > 0) && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="email-linked-list">
                {email.links.map((l) => (
                  <span key={`${l.type}-${l.id}`} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-[#2E5E3A] text-white" data-testid={`email-linked-${l.type}-${l.id}`}>
                    <CheckCircle2 className="w-3 h-3" /> <span className="uppercase text-[9px] opacity-80">{l.type}</span> {l.label}
                    <button className="ml-1 hover:opacity-70" onClick={() => unlinkEntity(l)} disabled={linking} data-testid={`email-unlink-${l.type}-${l.id}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <select className="input-field w-auto" value={linkType} onChange={(e) => setLinkType(e.target.value)} data-testid="email-link-type">
                {ENTITY_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <select className="input-field flex-1 min-w-[180px]" value={linkId} onChange={(e) => setLinkId(e.target.value)} data-testid="email-link-entity-select">
                <option value="">Select a {linkType}…</option>
                {(entities[linkType] || []).map((e) => <option key={e.id} value={e.id}>{entityLabel(linkType, e)}</option>)}
              </select>
              <button className="btn-primary" onClick={linkEntity} disabled={!linkId || linking} data-testid="email-link-btn"><Link2 className="w-4 h-4" /> Link</button>
            </div>
            <div className="mt-3 pt-3 border-t border-[#F0EDE5] flex items-center gap-2 flex-wrap">
              <button className="btn-secondary" onClick={createLrFromEmail} disabled={creatingLr} data-testid="email-create-lr-btn"><Plus className="w-4 h-4" /> {creatingLr ? "Creating LR…" : "Create new LR from this email"}</button>
              <span className="text-[11px] text-[#68665E]">AI reads the email + attachments and prefills the new LR.</span>
            </div>
            {linkType === "lr" && <div className="text-[11px] text-[#68665E] mt-2">Linking an LR also marks its work as started and auto-fills blank LR fields.</div>}
          </div>

          {/* Create reminder */}
          <div className="border border-[#E5E3DB] rounded p-4" data-testid="email-reminder-section">
            {!showReminder ? (
              <button className="btn-secondary" onClick={() => { setShowReminder(true); if (!remTitle) setRemTitle(`Follow up: ${email.subject || "email"}`); }} data-testid="email-reminder-open">
                <Bell className="w-4 h-4" /> Create reminder / task from this email
              </button>
            ) : (
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold flex items-center gap-2"><Bell className="w-3.5 h-3.5" /> New reminder</div>
                <input className="input-field w-full" placeholder="What needs to be done?" value={remTitle} onChange={(e) => setRemTitle(e.target.value)} data-testid="email-reminder-title" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="input-label flex items-center gap-1"><Users className="w-3 h-3" /> Assign to</label>
                    <input list="email-reminder-users" className="input-field" placeholder="Person's name" value={remAssignee} onChange={(e) => setRemAssignee(e.target.value)} data-testid="email-reminder-assignee" />
                    <datalist id="email-reminder-users">{users.map((u) => <option key={u.id} value={u.name} />)}</datalist>
                  </div>
                  <div><label className="input-label">Due date</label><input type="date" className="input-field" value={remDue} onChange={(e) => setRemDue(e.target.value)} data-testid="email-reminder-due" /></div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="btn-primary" onClick={createReminder} disabled={remSaving || !remTitle.trim()} data-testid="email-reminder-save"><Plus className="w-4 h-4" /> {remSaving ? "Saving…" : "Add to board"}</button>
                  <button className="btn-secondary" onClick={() => setShowReminder(false)} disabled={remSaving}>Cancel</button>
                </div>
                <div className="text-[11px] text-[#68665E]">This reminder will be linked back to this email on the Reminders board.</div>
              </div>
            )}
          </div>

          {/* Attachments */}
          {email.attachments?.length > 0 && (
            <div data-testid="email-attachments">
              <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold mb-2">Attachments ({email.attachments.length}) — AI has read text-based files</div>
              <div className="space-y-2">
                {email.attachments.map((a) => (
                  <div key={a.attachment_id} className="flex items-center justify-between gap-3 border border-[#E5E3DB] rounded px-3 py-2" data-testid={`attachment-${a.attachment_id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-[#C84B31] shrink-0" />
                      <span className="text-sm text-[#1A1A18] truncate">{a.filename}</span>
                      <span className="text-[10px] text-[#68665E]">{a.text_len > 0 ? "AI-read" : "not text"}</span>
                    </div>
                    <button className="btn-ghost shrink-0" onClick={() => download(a)} data-testid={`attachment-download-${a.attachment_id}`}><Download className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Conversation thread */}
          <div data-testid="email-thread">
            <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold mb-2">Conversation ({thread.length || 1})</div>
            <div className="space-y-3 max-h-[28rem] overflow-y-auto">
              {(thread.length ? thread : [email]).map((m, i) => (
                <div key={m.id || i} className="border border-[#E5E3DB] rounded p-3" data-testid={`email-thread-msg-${m.id || i}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-medium text-[#1A1A18] truncate">{m.sender || m.sender_email}</span>
                    <span className="text-[11px] text-[#68665E] shrink-0">{formatDateTime(m.internal_ts) || m.date_raw || ""}</span>
                  </div>
                  <div className="text-sm text-[#4A4842] whitespace-pre-wrap">{m.body || m.snippet}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Reply */}
          <div className="border-t border-[#E5E3DB] pt-4" data-testid="email-reply">
            {!showReply ? (
              <button className="btn-primary" onClick={() => setShowReply(true)} data-testid="email-reply-open">
                <Reply className="w-4 h-4" /> Reply to {email.sender_email || "sender"}
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <span className="text-[#68665E]">Replying to <span className="font-medium text-[#1A1A18]">{email.sender_email || email.sender}</span></span>
                  <label className="flex items-center gap-1.5 cursor-pointer text-[#1A1A18]" data-testid="email-reply-all-toggle">
                    <input type="checkbox" checked={replyAll} onChange={(e) => setReplyAll(e.target.checked)} /> Reply All
                  </label>
                  <button type="button" className="text-[#C84B31] hover:underline" onClick={() => setShowCcBcc((v) => !v)} data-testid="email-reply-ccbcc-toggle">{showCcBcc ? "Hide Cc/Bcc" : "Add Cc / Bcc"}</button>
                </div>
                {showCcBcc && (
                  <div className="grid grid-cols-1 gap-2">
                    <input className="input-field w-full" placeholder="Cc (comma separated emails)" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="email-reply-cc" />
                    <input className="input-field w-full" placeholder="Bcc (comma separated emails)" value={bcc} onChange={(e) => setBcc(e.target.value)} data-testid="email-reply-bcc" />
                  </div>
                )}
                {replyAll && <div className="text-[11px] text-[#68665E]" data-testid="email-reply-all-hint">Reply All will also Cc everyone on the original To/Cc lines.</div>}
                {templates.length > 0 && (
                  <div className="flex items-center gap-2" data-testid="email-reply-templates">
                    <select className="input-field w-auto text-sm" value="" onChange={(e) => { applyTemplate(e.target.value); e.target.value = ""; }} data-testid="email-template-select">
                      <option value="">Insert a saved template…</option>
                      {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                )}
                <textarea
                  rows={5}
                  className="input-field w-full resize-y"
                  placeholder="Type your reply…"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  data-testid="email-reply-textarea"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <button className="btn-primary" onClick={sendReply} disabled={replying || !replyText.trim()} data-testid="email-reply-send">
                    <Send className="w-4 h-4" /> {replying ? "Sending…" : "Send reply"}
                  </button>
                  <button className="btn-secondary" onClick={() => { setShowReply(false); setReplyText(""); }} disabled={replying} data-testid="email-reply-cancel">Cancel</button>
                  <button className="btn-ghost text-xs ml-auto" onClick={saveTemplate} disabled={!replyText.trim()} data-testid="email-reply-save-template">＋ Save as template</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Header({ email, connectedBy }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Company Mail</div>
      <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Inbox Assistant</h1>
      {email && <div className="text-sm text-[#68665E] mt-1" data-testid="inbox-connected-email">Shared inbox: {email}{connectedBy ? ` · connected by ${connectedBy}` : ""}</div>}
    </div>
  );
}
